import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  randBoolean,
  randPastDate,
  randRecentDate,
  randSentence,
  randChanceBoolean,
} from '@ngneat/falso'
import type { MockServer, MirageServer } from '../server.js'
import type { SchemaAttributes } from '../../types.js'
import { pageable, createHtmlParagraph } from '../utils.js'
import { subtractToDate } from '@/utils/date/calculation.js'
import { formatToDateTimeISO } from '@/utils/date/conversion.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import { safeMathRandom } from '@/utils/number.js'

export type ConsultationNoteVersionData =
  SchemaAttributes<'ConsultationNoteVersion'>

export const model = (
  Model as ModelDefinition<ConsultationNoteVersionData>
).extend({
  modifiedUser: belongsTo('user'),
})

export const factory = Factory.extend<ConsultationNoteVersionData>({
  text() {
    return createHtmlParagraph()
  },
  edit_reason() {
    return randChanceBoolean({ chanceTrue: 0.7 })
      ? randSentence({ length: 5 }).join(' ')
      : null
  },
  modified() {
    return randRecentDate().toISOString()
  },
  draft() {
    return randBoolean()
  },
  approved() {
    return randBoolean()
  },
  archived_at() {
    return randChanceBoolean({ chanceTrue: 0.1 })
      ? randPastDate().toISOString()
      : null
  },
  afterCreate(version, server) {
    // Create a modified user for each version
    const modifiedUser = server.create('user')
    version.update({ modifiedUser })
  },
})

export function routes(server: MockServer) {
  server.get(
    '/consultation_notes/:parent_lookup_consultation_note/versions',
    function (this: MirageServer, schema, request) {
      const consultationNoteId = request.params.parent_lookup_consultation_note

      // Find all versions for this consultation note
      const versions = schema.where('consultationNoteVersion', {
        consultationNoteId,
      })

      const { data, meta } = pageable(versions, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  const consultationNotes = server.db.consultationNotes.slice(0, 10)

  consultationNotes.forEach((note) => {
    const versionCount = Math.floor(safeMathRandom() * 6) + 2

    for (let i = 0; i < versionCount; i++) {
      server.create('consultationNoteVersion', {
        consultationNoteId: note.id,
        text: i === 0 ? note.text : createHtmlParagraph(),
        edit_reason: i === 0 ? null : `Version ${i + 1} edit`,
        modified: formatToDateTimeISO(
          subtractToDate(getTodayDateTimeISO(), { days: versionCount - i }),
        ),
        draft: i === versionCount - 1 ? note.draft : false,
        approved: i !== versionCount - 1,
      })
    }
  })
}
